import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTitle,
} from "@/components/ui/dialog";
import usePlans from "@/features/payments/hooks/use-plans";
import {
	CalendarDays,
	Shield,
	ShoppingBag,
	Tag,
	Users,
} from "lucide-react";

const FEATURES = [
	{
		icon: CalendarDays,
		title: "Monthly entries accumulate and never expire",
		description:
			"Your entries roll over and stay active while your plan is active.",
	},
	{
		icon: Users,
		title: "Weekly member draws with equal odds",
		description:
			"Get access to all member-only weekly draws with a standalone non-accumulating entry system.",
	},
	{
		icon: Tag,
		title: "Best value per entry",
		description: "Memberships give you more entries for less.",
	},
	{
		icon: ShoppingBag,
		title: "Save on epic gear",
		description: "Enjoy member discounts on our fishing merch and partner gear.",
	},
];

function SubscribeInsteadDialog({
	open,
	onOpenChange,
	onViewPlans,
	onProceedToEntry,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onViewPlans: () => void;
	onProceedToEntry: () => void;
}) {
	const { plans } = usePlans();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md p-8">
				<div className="flex flex-col items-center gap-1 text-center">
					<span className="text-xs font-bold uppercase tracking-widest text-blue-600">
						Members get more
					</span>
					<DialogTitle className="text-2xl font-bold leading-tight">
						Get more entries with a{" "}
						<span className="text-blue-600">membership</span>
					</DialogTitle>
					<p className="web-body-b3 mt-1 text-gray-600">
						Members are automatically entered into <strong>ALL</strong> draws
						while subscribed and get exclusive perks — the best value per entry.
					</p>
				</div>

				<div className="flex flex-col gap-4">
					{FEATURES.map(({ icon: Icon, title, description }) => (
						<div key={title} className="flex items-start gap-3">
							<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
								<Icon size={18} />
							</div>
							<div>
								<p className="web-body-b1-medium">{title}</p>
								<p className="web-body-b3 text-gray-500">{description}</p>
							</div>
						</div>
					))}
				</div>

				{plans.length > 0 && (
					<div className="flex flex-wrap justify-center gap-2">
						{plans.map((plan, index) => {
							const discountPct = Math.round(10 * (index + 1));
							return (
								<div
									key={plan.id}
									className="flex flex-col items-center rounded-xl bg-blue-50 px-4 py-2"
								>
									<span className="text-xs font-bold text-blue-700">
										{discountPct}% OFF
									</span>
									<span className="text-[11px] uppercase tracking-wide text-gray-500">
										{plan.name}
									</span>
								</div>
							);
						})}
					</div>
				)}

				<div className="flex items-center justify-center gap-1.5 text-gray-500">
					<Shield size={15} />
					<span className="web-body-b3">
						Cancel anytime. No lock-ins. No stress.
					</span>
				</div>

				<p className="web-body-b1-medium text-center">
					Want to check the plans before you buy?
				</p>

				<DialogFooter className="flex-col gap-3">
					<Button
						variant="secondary"
						className="w-full"
						onClick={onProceedToEntry}
					>
						Buy one-time entries
					</Button>
					<Button className="w-full" onClick={onViewPlans}>
						View membership plans
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export default SubscribeInsteadDialog;
